import React, { useCallback } from 'react';
import { db } from 'src/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { TextInputField } from 'evergreen-ui';
// import { Loading } from 'src/components';
// import Layout from 'src/layouts/Default';
import './styles.css';

const animFolders = {
  'idle-1': {
    animation: 'looping',
    maxFrames: 33,
    preloadedFrames: [],
  },
  'idle-2': {
    animation: 'looping',
    maxFrames: 8,
    preloadedFrames: [],
  },
  'scroll-1': {
    animation: 'scrolling',
    maxFrames: 57,
    preloadedFrames: [],
  },
  'scroll-2': {
    animation: 'scrolling',
    maxFrames: 60,
    preloadedFrames: [],
  },
  'idle-3': {
    animation: 'looping',
    maxFrames: 14,
    preloadedFrames: [],
  },
};

const getPreviousFolderMaxFrames = (folder) => {
  if (folder === 'scroll-1') return 0;
  if (folder === 'scroll-2') return animFolders['scroll-1'].maxFrames;
  return 0;
};

const maxFrameCount = Object.values(animFolders).reduce((acc, val) => {
  if (val.animation === 'scrolling') {
    return acc + val.maxFrames;
  }
  return acc;
}, 0);

const getFrameIndexScrolling = (scrollFraction, folder) => Math.min(maxFrameCount - 1, Math.ceil(scrollFraction * maxFrameCount)) - getPreviousFolderMaxFrames(folder);
const getFrameIndexLooping = (lastFrameIndex, folder) => {
  if (lastFrameIndex + 1 >= animFolders[folder].maxFrames) {
    return 1;
  }

  return lastFrameIndex + 1;
};

const getFrame = (folder, frameIndex) => {
  if (animFolders[folder].preloadedFrames[frameIndex]) {
    return animFolders[folder].preloadedFrames[frameIndex];
  }

  const frame = `${frameIndex}`.padStart(2, '0');
  return `/anim/${folder}/${frame}.png`;
};

const drawFrame = (ctx, canvas, folder, frameIndex) => {
  const img = new Image();
  img.src = getFrame(folder, frameIndex);
  img.onload = () => {
    if (ctx && ctx.drawImage) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    }
  };
};

const preloadImage = async (imgUrl, folder, index) => {
  try {
    const imgResponse = await fetch(imgUrl);
    const imgBlob = await imgResponse.blob();
    const img = URL.createObjectURL(imgBlob);
    animFolders[folder].preloadedFrames[index] = img;
    return img;
  } catch (e) {
    console.error(e);
  }
};

const preloadImages = () =>
  new Promise(async (resolve) => {
    const preloadImageParams = [];
    for (let folder in animFolders) {
      for (let i = 1; i <= animFolders[folder].maxFrames; i++) {
        const imgUrl = getFrame(folder, i);
        preloadImageParams.push({ imgUrl, folder, index: i });
      }
    }

    await Promise.allSettled(preloadImageParams.map(({ imgUrl, folder, index }) => preloadImage(imgUrl, folder, index)));
    resolve();
  });

const Subscribe = () => {
  const [isSubscribing, setIsSubscribing] = React.useState(false);
  const [isSubscribeComplete, setIsComplete] = React.useState(false);
  const [areImagesPreloaded, setAreImagesPreloaded] = React.useState(false);
  const [hasStartedAnimation, setHasStartedAnimation] = React.useState(false);
  const [isAnimationComplete, setIsAnimationComplete] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [isEmailValid, setIsEmailValid] = React.useState(true);
  const inputRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const frameNumber = React.useRef(1);
  const folder = React.useRef('idle-1');
  const animation = React.useRef('looping');
  const loopingTimer = React.useRef();
  const ctx = React.useRef();
  const getScrollContainer = useCallback(() => document.querySelector('.container'), []);

  const shouldMoveFolders = React.useCallback(() => {
    const html = getScrollContainer();
    const scrollTop = html.scrollTop;
    const maxScrollTop = html.scrollHeight - window.innerHeight;
    switch (folder.current) {
      case 'idle-1':
        if (frameNumber.current + 1 >= animFolders[folder.current].maxFrames) {
          animation.current = 'looping';
          folder.current = 'idle-2';
          frameNumber.current = 1;
        }
        if (scrollTop > 1) {
          animation.current = 'scrolling';
          folder.current = 'scroll-1';
          frameNumber.current = 1;
        }
        break;
      case 'idle-2':
        if (scrollTop > 1) {
          animation.current = 'scrolling';
          folder.current = 'scroll-1';
          frameNumber.current = 1;
        }
        break;
      case 'scroll-1':
        if (frameNumber.current >= animFolders[folder.current].maxFrames) {
          animation.current = 'scrolling';
          folder.current = 'scroll-2';
          frameNumber.current = 1;
        }
        if (scrollTop < 1) {
          animation.current = 'looping';
          folder.current = 'idle-1';
          frameNumber.current = 1;
        }
        break;
      case 'scroll-2':
        if (frameNumber.current <= 1) {
          animation.current = 'scrolling';
          folder.current = 'scroll-1';
          frameNumber.current = 1;
        }
        if (scrollTop >= maxScrollTop || frameNumber.current + 2 >= animFolders['scroll-2'].maxFrames || scrollTop + 30 >= maxScrollTop) {
          document.querySelector('html').style.overflow = 'unset'
          html.scrollTo({
            top: maxScrollTop,
            left: 0,
            behavior: "smooth",
          });
          animation.current = 'looping';
          folder.current = 'idle-3';
          frameNumber.current = 1;
          setIsAnimationComplete(true);
        }
        break;
      case 'idle-3':
        break;
      default:
    }
  }, [getScrollContainer]);

  const onNextFrameLooping = React.useCallback(() => {
    if (!hasStartedAnimation) return;

    if (animation.current === 'looping') {
      if (folder.current === 'idle-1') {
        shouldMoveFolders();
      }
      const frameIndex = getFrameIndexLooping(frameNumber.current, folder.current);
      frameNumber.current = frameIndex;
      drawFrame(ctx.current, canvasRef.current, folder.current, frameNumber.current);
    }
  }, [hasStartedAnimation, shouldMoveFolders]);

  const onWindowScroll = React.useCallback(() => {
    if (!hasStartedAnimation || isAnimationComplete) return;

    if (animation.current === 'scrolling') {
      const html = getScrollContainer();

      const scrollTop = html.scrollTop;
      const maxScrollTop = html.scrollHeight - window.innerHeight;
      const scrollFraction = scrollTop / maxScrollTop;

      const frameIndex = getFrameIndexScrolling(scrollFraction, folder.current);
      frameNumber.current = frameIndex;
      drawFrame(ctx.current, canvasRef.current, folder.current, frameNumber.current);
    }
    shouldMoveFolders();
  }, [getScrollContainer, hasStartedAnimation, isAnimationComplete, shouldMoveFolders]);

  const preload = React.useCallback(async () => {
    getScrollContainer().scrollTo(0, 0);
    await preloadImages();
    setTimeout(() => {
      setAreImagesPreloaded(true);
    }, 1000);
  }, [getScrollContainer]);

  const detectAndroid = React.useCallback(async () => {
    const isAndroid = navigator.userAgent.match(/android/i)
    if (isAndroid) {
      const container = getScrollContainer();
      container.classList.add('android');
    }

    const isIOS = navigator.userAgent.match(/(iPad|iPhone|iPod)/i)
    if (isIOS) {
      const container = getScrollContainer();
      container.classList.add('ios');
    }

  }, [getScrollContainer]);

  React.useEffect(() => {
    detectAndroid();
    preload();

    const canvas = canvasRef.current;
    canvas.width = 2048;
    canvas.height = 2048;
    ctx.current = canvas.getContext('2d');

    drawFrame(ctx.current, canvasRef.current, folder.current, frameNumber.current);

    loopingTimer.current = setInterval(onNextFrameLooping, 150);

    const container = getScrollContainer();
    container.addEventListener('scroll', onWindowScroll);

    return () => {
      container.removeEventListener('scroll', onWindowScroll);
    };
  }, [detectAndroid, getScrollContainer, onNextFrameLooping, onWindowScroll, preload]);

  React.useEffect(() => {
    if (areImagesPreloaded && !hasStartedAnimation) {
      setHasStartedAnimation(true);
      getScrollContainer().scrollTo(0, 0);
    }
  }, [areImagesPreloaded, getScrollContainer, hasStartedAnimation]);

  const onEmailChange = (e) => {
    setEmail(e.target.value);
    return;
  };

  const onSubscribe = async () => {
    inputRef.current.blur();
    const { valid } = inputRef.current.validity;
    const validity = valid && !!email;
    setIsEmailValid(validity);
    if (!validity) return;

    setIsSubscribing(true);

    // Use the email as the key
    const docRef = doc(db, 'subscribers', email);
    await setDoc(docRef, {
      email,
    });

    setTimeout(() => {
      setIsSubscribing(false);
      setIsComplete(true);
      setEmail('');
    }, 850);

    setTimeout(() => {
      setEmail('');
      setIsComplete(false);
    }, 4000);
  };

  return (
    <div className='inner-container' style={isAnimationComplete ? { height: '96vh' } : undefined}>
      {areImagesPreloaded ? (
        <></>
      ) : (
        <div className="image-preloader">
          <img src="/loading.gif" alt="bagl preload" />
        </div>
      )}
      <canvas ref={canvasRef}></canvas>
      <form onSubmit={(e) => e.preventDefault()} className={`floating-input ${isAnimationComplete ? 'visible' : 'hidden'}`}>
        <div className={`input-box${isSubscribing ? ' disabled' : ''}`}>
          <img src="/input_box.png" alt="input box background" className="input-box-bg" draggable="false" />
          <TextInputField
            id="email"
            type="email"
            label={undefined}
            ref={inputRef}
            placeholder={isSubscribeComplete ? 'Subscribed' : 'Enter your email'}
            value={email}
            onChange={onEmailChange}
            isInvalid={!isEmailValid}
            validationMessage={isEmailValid ? undefined : 'A valid email address is required.'}
            disabled={isSubscribing}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSubscribe();
              }
            }}
          />
        </div>
      </form>
    </div>
  );
};

export default Subscribe;
